import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { UserSessionModel } from '../../../core/models/user/current-user.model';
import { CurrentUserService } from '../../../core/services/auth/current-user.service';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../core/services/auth/auth.service';
import { GoogleQrCodeDetailsModel } from '../../../core/models/user/google-qr-code-details.model';
import { CookieService } from '../../../core/services/cookie.service';
import { AccountService } from '../../../core/services/account/account.service';
import { Subscription } from 'rxjs';
import { SidebarComponent } from '../../../layout/sidebar/sidebar.component';
import { SystemInformationService } from '../../../core/services/systemInformation/system-information.service';
import { SystemInformationModel } from '../../../core/models/systemInformation/system-information.model';
import { AuthenticationResponse } from '../../../core/models/authentication-response/authentication-response.model';
import { ToastService } from '../../../core/services/toast.service';
import { UserService } from '../../../core/services/user/user.service';
import {
  UpdateUserRequest,
  UploadAvatarResponse,
} from 'app/core/models/user/user.model';
import { BaseApiResponse } from 'app/core/models/api/api.model';
import { SelectComponent } from 'app/shared/components/select/select.component';
import { LanguageService } from 'app/core/services/language.service';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { SearchCountryField, CountryISO } from 'ngx-intl-tel-input';
import { PhoneInputDirective } from 'app/shared/directives/phone-input.directive';
import { getCountryCodeFromDialCode } from '../../../utils/country-code-utils';

declare var bootstrap: any;

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    SelectComponent,
    NgxIntlTelInputModule,
    PhoneInputDirective,
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
})
export class ProfileComponent implements OnInit, OnDestroy {
  @ViewChild(SidebarComponent) sidebarComponent!: SidebarComponent;

  generalForm: FormGroup;
  authForm: FormGroup;
  googleLoginForm: FormGroup;
  googleQrCodeForm: FormGroup;
  mailCodeForm: FormGroup;
  mailLoginForm: FormGroup;
  passwordForm: FormGroup;

  generalVisible: boolean = true;
  activeTab: string = 'general';
  title: string = '';

  showPasswordArea: 'oldPassword' | 'newPassword' | 'confirmPassword';
  oldPasswordVisible: boolean = false;
  newPasswordVisible: boolean = false;
  confirmPasswordVisible: boolean = false;
  currentPasswordError: string | null = null;

  userId: string = '';
  organizationList: any = [];
  maxDate: string;
  fullName: string = '';
  currentUser: UserSessionModel | null = null;

  currentUserProfileData: any = null;

  isOverlayLoading: boolean = false;
  file: File | null = null;
  fileCount: any = null;
  fileSelector = document.createElement('input');
  tempProfilePhoto = '';
  isProfilePhotoUpdated: boolean = false;
  isDeleteClicked: boolean = false;
  previewImage: string | ArrayBuffer | null = null;

  showMailSection: boolean = false;
  mailAuthSectionLoading: boolean = false;
  isMailAuthPending: boolean = false;
  contentHeader: any = {};
  googleQrCodeDetails: GoogleQrCodeDetailsModel = {
    qrCodeImageUrl: '',
    manuelEntrySetupCode: '',
  };

  googleAuthToggle = false;
  isGoogleActivated: boolean = false;
  isGoogleChecked: boolean = false;
  isMailChecked: boolean = false;
  questions: { id: number; question: string }[] = [];

  langChangeSubscription: Subscription | null = null;
  currentPath: string | null = null;
  systemInformation!: SystemInformationModel;

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  defaultCountry: CountryISO = CountryISO.Turkey;

  constructor(
    private userService: UserService,
    private currentUserService: CurrentUserService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private readonly systemInformationService: SystemInformationService,
    private toastService: ToastService,
    private router: Router,
    public languageService: LanguageService,
  ) {
    const userLang = localStorage.getItem('app-lang') || 'en-US';

    this.translate.use(userLang).subscribe(() => {
      this.translate.get('profile.general').subscribe((translation: string) => {
        this.title = translation;
      });
    });

    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];

    // --- Form Initialization ---
    this.generalForm = this.fb.group({
      id: [''],
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(?!.*[ğĞçÇşŞıİ])([a-zA-ZÀ-ÿĲĳ\s]*)$/u),
        ],
      ],
      surname: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(?!.*[ğĞçÇşŞıİ])([a-zA-ZÀ-ÿĲĳ\s]*)$/u),
        ],
      ],
      phoneNumber: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      selectedLanguageCode: ['', [Validators.required]],
    });

    this.authForm = this.fb.group({
      googleAuth: [{ value: false, disabled: false }],
      mailAuth: [{ value: false, disabled: false }],
    });

    this.googleQrCodeForm = this.fb.group({
      code: ['', [Validators.required]],
    });
    this.googleLoginForm = this.fb.group({
      code: ['', [Validators.required]],
    });
    this.mailCodeForm = this.fb.group({
      code: ['', [Validators.required]],
    });
    this.mailLoginForm = this.fb.group({
      code: ['', [Validators.required]],
    });

    this.passwordForm = this.fb.group(
      {
        currentPassword: ['', [Validators.required]],
        newPassword: [
          '',
          [Validators.required, this.passwordStrengthValidator.bind(this)],
        ],
        confirmNewPassword: ['', [Validators.required]],
      },
      {
        validators: [
          this.passwordMatchValidator.bind(this),
          this.newPasswordDifferentFromCurrent.bind(this),
        ],
      },
    );
  }

  ngOnInit(): void {
    this.currentPath = this.route.snapshot.routeConfig?.path;
    this.currentUser = this.currentUserService.GetSession();

    this.getUserById();

    this.contentHeader = {
      headerTitle: this.translate.instant('navbar.profile'),
      actionButton: false,
    };
    const modalElement = document.getElementById('createGoogleQrCodeModal');
    if (modalElement) {
      modalElement.addEventListener('hidden.bs.modal', () => {
        this.updateAuthFormValues();
      });
    }
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  getSystemInformation() {
    this.systemInformationService.getSystemInformation().subscribe({
      next: (res) => {
        if (res[0] && !res[0].id) return;
        this.systemInformation = res[0];
      },
    });
  }

  getUserById() {
    this.userService.getCurrentUserProfile().subscribe({
      next: (res) => {
        if (!res || !res.isSuccess || !res.data) return;

        const data = res.data;
        this.currentUserProfileData = data;

        this.generalForm.patchValue({
          id: data.id,
          name: data.name,
          surname: data.lastName,
          email: data.email,
          phoneNumber: data.phone,
          selectedLanguageCode:
            this.currentUser?.selectedLanguageCode || 'en-US',
        });

        this.fullName = `${data.name} ${data.lastName}`;
        
        // Set the default country based on the user's country code
        if (data.countryCode) {
          const countryCode = getCountryCodeFromDialCode(data.countryCode);
          if (countryCode) {
            this.defaultCountry = countryCode as CountryISO;
          }
        }
        
        const authType = data.authenticatorType;

        let googleEnabled = false;
        let mailEnabled = false;

        if (authType === 1) {
          googleEnabled = true;
          this.isGoogleActivated = true;
        } else if (authType === 2) {
          mailEnabled = true;
        }

        this.authForm.patchValue({
          googleAuth: googleEnabled,
          mailAuth: mailEnabled,
        });

        this.googleAuthToggle = googleEnabled;
        this.isMailChecked = mailEnabled;

        if (googleEnabled) {
          this.authForm.get('mailAuth')?.disable();
          this.authForm.get('googleAuth')?.enable();
        } else if (mailEnabled) {
          this.authForm.get('googleAuth')?.disable();
          this.authForm.get('mailAuth')?.enable();
        } else {
          this.authForm.get('googleAuth')?.enable();
          this.authForm.get('mailAuth')?.enable();
        }

        if (data.avatar) {
          this.previewImage = data.avatar.startsWith('data:image')
            ? data.avatar
            : `data:image/jpeg;base64,${data.avatar}`;
        } else {
          this.previewImage = 'assets/media/profile/profile.svg';
        }
      },
      error: (err) => {
        console.error('Error', err);
      },
    });
  }

  save(formType: string) {
    if (formType === 'generalForm') {
      if (this.generalForm.invalid) {
        this.generalForm.markAllAsTouched();
        return;
      }

      if (!this.currentUserProfileData) {
        this.toastService.showToast(
          this.translate.instant('errorOccurred'),
          'error',
        );
        return;
      }
      const roleId = this.currentUserProfileData.role?.id || null;

      let userOrganizationFacility = null;

      if (
        this.currentUserProfileData.userOrganizations &&
        this.currentUserProfileData.userOrganizations.length > 0
      ) {
        const firstOrg = this.currentUserProfileData.userOrganizations[0];

        userOrganizationFacility = {
          organizationId: firstOrg.organizationId,
          organizationName: firstOrg.organizationName,
          facilities: firstOrg.userFacilities
            ? firstOrg.userFacilities.map((f: any) => ({
                facilityId: f.facilityId,
                facilityName: f.facilityName,
              }))
            : [],
        };
      }

      const phoneValue = this.generalForm.get('phoneNumber')?.value;
      const phoneNumber = phoneValue?.number
        ? phoneValue.number.replace(/\D/g, '')
        : '';
      const countryCode = phoneValue?.dialCode;

      const request: any = {
        id: this.currentUserProfileData.id,
        name: this.generalForm.get('name')?.value,
        surName: this.generalForm.get('surname')?.value,
        email: this.generalForm.get('email')?.value,
        phoneNumber: phoneNumber,

        countryCode: countryCode,
        birthDate:
          this.currentUserProfileData.birthDate || new Date().toISOString(),
        gender: this.currentUserProfileData.gender || 1,
        pid: this.currentUserProfileData.pid || null,

        roleId: roleId,
        isProfile: true,
        avatar: this.currentUserProfileData.avatar || null,

        userOrganizationFacility: userOrganizationFacility,
      };

      this.userService.updateUserProfile(request).subscribe({
        next: (res) => {
          if (!res.fail) {
            this.toastService.showToast(
              this.translate.instant('informations.accountUpdated'),
              'success',
            );

            const selectedLang = this.generalForm.get(
              'selectedLanguageCode',
            )?.value;
            if (selectedLang) {
              this.setLanguage(selectedLang);
            }
            this.updateCurrentUserSession({
              name: request.name,
              surname: request.surName,
              email: request.email,
              phoneNumber: request.phoneNumber,
              languageCode: selectedLang,
            });

            this.fullName = `${request.name} ${request.surName}`;
          } else {
            this.toastService.showToast(
              this.translate.instant('errorOccurred'),
              'error',
            );
          }
        },
        error: (err) => {
          this.toastService.showToast(
            this.translate.instant('errorOccurred'),
            'error',
          );
          console.error(err);
        },
      });
    }
  }

  setLanguage(lang: string) {
    this.translate.use(lang);
    localStorage.setItem('app-lang', lang);
  }

  updateCurrentUserSession(data: any) {
    let currentUser = this.currentUserService.GetSession();
    if (currentUser) {
      currentUser.name = data.name;
      currentUser.lastName = data.surname;
      currentUser.phoneNumber = data.phoneNumber;
      currentUser.email = data.email;
      if (data.languageCode) {
        currentUser.selectedLanguageCode = data.languageCode;
      }
      this.currentUserService.SetSession(currentUser);
    }
  }

  // --- HELPER FUNCTIONS ---
  updateAuthFormValues() {
    const session = this.currentUserService.GetSession();
    const authType = session?.authenticatorType;

    const isGoogle = authType === 1;
    const isMail = authType === 2;

    this.googleAuthToggle = isGoogle;
    this.isGoogleActivated = isGoogle;
    this.isMailChecked = isMail;
    this.isMailAuthPending = false;

    this.authForm.patchValue({
      googleAuth: isGoogle,
      mailAuth: isMail,
    });

    if (isGoogle) {
      this.authForm.get('mailAuth')?.disable();
      this.authForm.get('googleAuth')?.enable();
      this.showMailSection = false;
    } else if (isMail) {
      this.authForm.get('googleAuth')?.disable();
      this.authForm.get('mailAuth')?.enable();
    } else {
      this.authForm.get('googleAuth')?.enable();
      this.authForm.get('mailAuth')?.enable();
      this.showMailSection = false;
    }

    this.googleQrCodeForm.reset();
    this.mailCodeForm.reset();
  }

  // --- GOOGLE AUTHENTICATION FLOW ---
  onGoogleAuthToggle(e: Event): void {
    const isChecked = (e.target as HTMLInputElement).checked;
    this.isGoogleChecked = isChecked;

    if (isChecked) {
      this.authForm.get('mailAuth')?.disable();

      // Get QR Code
      this.authService.getGoogleAuth().subscribe({
        next: (res: GoogleQrCodeDetailsModel) => {
          this.googleQrCodeDetails.qrCodeImageUrl = res?.qrCodeImageUrl;
          this.googleQrCodeDetails.manuelEntrySetupCode =
            res?.manuelEntrySetupCode;

          const modal = new bootstrap.Modal(
            document.getElementById('createGoogleQrCodeModal'),
          );
          modal.show();
        },
        error: () => {
          this.updateAuthFormValues();
        },
      });
    } else {
      this.googleQrCodeDetails.qrCodeImageUrl = '';

      const modal = new bootstrap.Modal(
        document.getElementById('createGoogleQrCodeModal'),
      );
      modal.show();
    }
  }

  submitGoogleQrCodeForm(e: Event): void {
    e.preventDefault();

    if (!this.googleQrCodeForm.valid) {
      this.handleInvalidForm(this.googleQrCodeForm);
      return;
    }

    const formData = { ...this.googleQrCodeForm.value };

    if (this.isGoogleChecked) {
      this.handleEnableGoogleAuth(formData);
    } else {
      this.handleDisableGoogleAuth(formData);
    }
  }

  private handleEnableGoogleAuth(formData: any): void {
    this.authService.enableGoogleAuth(formData).subscribe({
      next: (res: AuthenticationResponse) => {
        this.handleAuthResponse(res, true);
      },
      error: (error) => {
        if (error.error?.fail?.Code) {
          // Set the invalidCode error on the form control
          this.googleQrCodeForm.get('code')?.setErrors({ invalidCode: true });
          this.googleQrCodeForm.get('code')?.markAsTouched();
        }
      },
    });
  }

  private handleDisableGoogleAuth(formData: any): void {
    this.authService.disableGoogleAuth(formData).subscribe({
      next: (res: AuthenticationResponse) => {
        this.handleAuthResponse(res, false);
      },
      error: (error) => {
        if (error.error?.fail?.Code) {
          // Set the invalidCode error on the form control
          this.googleQrCodeForm.get('code')?.setErrors({ invalidCode: true });
          this.googleQrCodeForm.get('code')?.markAsTouched();
        }
      },
    });
  }

  private handleAuthResponse(
    res: AuthenticationResponse,
    isEnableProcess: boolean,
  ): void {
    const modalElement = document.getElementById('createGoogleQrCodeModal');
    const modal = bootstrap.Modal.getInstance(modalElement);

    if (res.result) {
      modal?.hide();

      let currentSession = this.currentUserService.GetSession();
      if (currentSession) {
        currentSession.authenticatorType = isEnableProcess ? 1 : 0;
        this.currentUserService.SetSession(currentSession);
      }

      // Logout user after successful 2FA enablement
      if (isEnableProcess) {
        this.toastService.showToast(
          this.translate.instant('authenticationEnabledLoggingOut'),
          'success',
        );
        setTimeout(() => {
          this.authService.logout().subscribe(() => {
            localStorage.setItem('isLogin', 'false');
          });
        }, 5000);
      } else {
        this.toastService.showToast(
          this.translate.instant('authenticationDisabled'),
          'success',
        );
        this.updateAuthFormValues();
      }
    } else {
      this.googleQrCodeForm.get('code')?.setErrors({ invalidCode: true });
      this.googleQrCodeForm.get('code')?.markAsTouched();
    }
  }

  // --- MAIL AUTHENTICATION FLOW ---

  onMailAuthToggle(e: Event): void {
    const isChecked = (e.target as HTMLInputElement).checked;
    const currentAuthType =
      this.currentUserService.GetSession()?.authenticatorType;

    if (this.isMailAuthPending) {
      this.showMailSection = !this.showMailSection;
      return;
    }

    if (isChecked) {
      if (currentAuthType === 2) {
        this.showMailSection = false;
        this.mailCodeForm.reset();
        return;
      }
      this.isMailAuthPending = true;

      this.authService.sendEmailAuthForEnable().subscribe({
        next: () => {
          this.authForm.get('mailAuth')?.patchValue(true);
          this.showMailSection = true;
          this.toastService.showToast(
            this.translate.instant('verificationCodeSent'),
            'success',
          );
        },
        error: () => {
          this.isMailAuthPending = false;
          this.authForm.get('mailAuth')?.patchValue(false);
        },
      });
    } else {
      const formControlValue = this.authForm.get('mailAuth')?.value;
      
      if (formControlValue === false && currentAuthType !== 2) {
        this.authForm.get('mailAuth')?.patchValue(false);
        this.showMailSection = false;
        this.mailCodeForm.reset();
        return;
      }
      this.isMailAuthPending = true;
      this.authService.sendEmailAuthForDisable().subscribe({
        next: () => {
          this.authForm.get('mailAuth')?.patchValue(false);
          this.showMailSection = true;
          this.toastService.showToast(
            this.translate.instant('verificationCodeSent'),
            'success',
          );
        },
        error: () => {
          this.isMailAuthPending = false;
          this.authForm.get('mailAuth')?.patchValue(true);
        },
      });
    }
  }

  submitMailAuthCode(e: Event): void {
    e.preventDefault();

    if (!this.mailCodeForm.valid) {
      this.handleInvalidForm(this.mailCodeForm);
      return;
    }

    const formData = { ...this.mailCodeForm.value };
    const isEnablingMailAuth = this.authForm.get('mailAuth')?.value ?? false;

    console.log('[submitMailAuthCode] isEnablingMailAuth:', isEnablingMailAuth);

    if (isEnablingMailAuth) {
      this.handleEnableMailAuth(formData);
    } else {
      this.handleDisableMailAuth(formData);
    }
  }

  private handleEnableMailAuth(formData: any): void {
    this.mailAuthSectionLoading = true;
    this.authService.enableEmailAuth(formData).subscribe({
      next: (res: AuthenticationResponse) => {
        this.handleMailAuthResponse(res, true);
      },
      error: (err) => {
        this.mailAuthSectionLoading = false;
        this.isMailAuthPending = false;
        this.toastService.showToast(
          this.translate.instant('errorOccurred'),
          'error',
        );
      },
    });
  }

  private handleDisableMailAuth(formData: any): void {
    this.mailAuthSectionLoading = true;
    this.authService.disableEmailAuth(formData).subscribe({
      next: (res: AuthenticationResponse) => {
        this.handleMailAuthResponse(res, false);
      },
      error: (err) => {
        this.mailAuthSectionLoading = false;
        this.isMailAuthPending = false;
        this.toastService.showToast(
          this.translate.instant('errorOccurred'),
          'error',
        );
      },
    });
  }

  private handleMailAuthResponse(
    res: AuthenticationResponse,
    isEnableProcess: boolean,
  ): void {
    this.mailAuthSectionLoading = false;
    this.isMailAuthPending = false;

    if (res.result) {
      this.showMailSection = false;

      let currentSession = this.currentUserService.GetSession();
      if (currentSession) {
        currentSession.authenticatorType = isEnableProcess ? 2 : 0;
        this.currentUserService.SetSession(currentSession);
      }

      // Logout user after successful email auth enablement only
      if (isEnableProcess) {
        setTimeout(() => {
          this.authService.logout().subscribe(() => {
            localStorage.setItem('isLogin', 'false');
          });
        }, 1500);
      } else {
        if (isEnableProcess) {
        this.toastService.showToast(
          this.translate.instant('authenticationEnabledLoggingOut'),
          'success',
        );
        setTimeout(() => {
          this.authService.logout().subscribe(() => {
            localStorage.setItem('isLogin', 'false');
          });
        }, 5000);
      } else {
        this.toastService.showToast(
          this.translate.instant('authenticationDisabled'),
          'success',
        );
        this.updateAuthFormValues();
      }
      }
    } else {
      this.toastService.showToast(
        this.translate.instant('codeNotCorrect'),
        'error',
      );
      this.mailCodeForm.reset();
    }
  }

  // --- HELPERS ---
  private handleInvalidForm(form: FormGroup): void {
    form.markAllAsTouched();
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.generalForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  isGoogleLoginControlInvalid(controlName: string): boolean {
    const control = this.googleLoginForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  isFirstLoginControlInvalid(controlName: string): boolean {
    const control = this.mailLoginForm.get(controlName);
    const codeControl = this.mailCodeForm.get(controlName);
    return codeControl ? codeControl.invalid && codeControl.touched : false;
  }

  showPassword(showPasswordArea: string): void {
    switch (showPasswordArea) {
      case 'oldPassword':
        this.oldPasswordVisible = !this.oldPasswordVisible;
        break;
      case 'newPassword':
        this.newPasswordVisible = !this.newPasswordVisible;
        break;
      case 'confirmPassword':
        this.confirmPasswordVisible = !this.confirmPasswordVisible;
        break;
    }
  }

  handleFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.fileCount = input.files.length;
      this.file = input.files[0];
      input.value = '';
    }
  }

  onFileSelected(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file: File = input.files[0];
      const reader: FileReader = new FileReader();
      reader.onload = (e) => (this.previewImage = e.target?.result);
      reader.readAsDataURL(file);

      const formData: FormData = new FormData();
      formData.append('Avatar', file);
      formData.append('UserId', this.currentUser.id);

      this.userService.uploadAvatar(formData).subscribe({
        next: async (data: BaseApiResponse<UploadAvatarResponse>) => {
          if (data && data.isSuccess) {
            this.toastService.showToast(
              this.translate.instant('informations.profilePhotoUpdated'),
              'success',
            );
            // Notify sidebar about avatar change
            this.currentUserService.notifyAvatarChanged(
              this.previewImage as string,
            );
          }
        },
      });
    }
  }

  deleteAvatar(): void {
    if (!this.currentUser?.id) {
      this.toastService.showToast(
        this.translate.instant('errorOccurred'),
        'error',
      );
      return;
    }

    this.userService.deleteAvatar(this.currentUser.id).subscribe({
      next: (data: BaseApiResponse<any>) => {
        if (data && data.isSuccess) {
          this.previewImage = 'assets/media/profile/profile.svg';
          if (this.currentUserProfileData) {
            this.currentUserProfileData.avatar = null;
          }
          this.toastService.showToast(
            this.translate.instant('informations.avatarDeleted'),
            'success',
          );
          // Notify sidebar about avatar removal
          this.currentUserService.notifyAvatarChanged(null);
        } else {
          this.toastService.showToast(
            this.translate.instant('errorOccurred'),
            'error',
          );
        }
      },
    });
  }

  get isFileSizeValid() {
    if (this.fileSelector.files && this.fileSelector.files.length > 0) {
      return !(this.fileSelector.files[0].size > 1000000);
    }
    return true;
  }

  setTitle(title: string) {
    if (title === 'general')
      this.title = this.translate.instant('profile.general');
    else if (title === 'authentication')
      this.title = this.translate.instant('profile.authentication');
    else if (title === 'password')
      this.title = this.translate.instant('profile.password');
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('newPassword')?.value;
    const confirmNewPassword = control.get('confirmNewPassword')?.value;

    if (
      newPassword &&
      confirmNewPassword &&
      newPassword !== confirmNewPassword
    ) {
      return { passwordMismatch: true };
    }
    return null;
  }

  newPasswordDifferentFromCurrent(
    control: AbstractControl,
  ): ValidationErrors | null {
    const currentPassword = control.get('currentPassword')?.value;
    const newPassword = control.get('newPassword')?.value;

    if (currentPassword && newPassword && currentPassword === newPassword) {
      return { sameAsCurrentPassword: true };
    }
    return null;
  }

  passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    const errors: ValidationErrors = {};

    if (value.length < 8) {
      errors['minLength'] = true;
    }

    if (value.length > 16) {
      errors['maxLength'] = true;
    }

    if (!/[A-Z]/.test(value)) {
      errors['uppercase'] = true;
    }
    if (!/[a-z]/.test(value)) {
      errors['lowercase'] = true;
    }

    if (!/[0-9]/.test(value)) {
      errors['number'] = true;
    }
    if (!/[^a-zA-Z0-9]/.test(value)) {
      errors['specialChar'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  isPasswordControlInvalid(controlName: string): boolean {
    const control = this.passwordForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  changePassword(): void {
    this.currentPasswordError = null;

    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const request = {
      currentPassword: this.passwordForm.get('currentPassword')?.value,
      newPassword: this.passwordForm.get('newPassword')?.value,
      confirmNewPassword: this.passwordForm.get('confirmNewPassword')?.value,
    };

    this.authService.changePassword(request).subscribe({
      next: (res) => {
        if (res.isSuccess) {
          this.toastService.showToast(
            this.translate.instant('informations.newPasswordSetted'),
            'success',
          );

          this.passwordForm.reset();
          this.passwordForm.markAsPristine();
          this.passwordForm.markAsUntouched();
          this.currentPasswordError = null;
          this.authService.logout().subscribe(() => {
            localStorage.setItem('isLogin', 'false');
          });
        } else {
          const errors = res.Errors || res.errors || [];
          const errorMessage = errors[0] || '';

          if (
            errorMessage.includes('IncorrectCurrentPassword') ||
            errorMessage.toLowerCase().includes('incorrect') ||
            errorMessage.toLowerCase().includes('wrong')
          ) {
            this.currentPasswordError = this.translate.instant(
              'alerts.incorrectCurrentPassword',
            );
            this.toastService.showToast(
              this.translate.instant('alerts.incorrectCurrentPassword'),
              'error',
            );
          } else if (
            errorMessage.includes('PasswordUsedBefore') ||
            errorMessage.includes('PasswordUserBefore') ||
            errorMessage.toLowerCase().includes('used before')
          ) {
            this.toastService.showToast(
              this.translate.instant('alerts.passwordUsedBefore'),
              'error',
            );
          } else {
            this.toastService.showToast(
              this.translate.instant('alerts.passwordUpdateFailed'),
              'error',
            );
          }
        }
      },
      error: (err) => {
        // Handle specific backend errors
        if (err.status === 400) {
          // Check backend error structure: err.error.fail.title
          const errorTitle = err.error?.fail?.title || err.error?.title;

          // Check for specific error types
          if (errorTitle === 'IncorrectCurrentPassword') {
            // Set inline error for current password field
            this.currentPasswordError = this.translate.instant(
              'alerts.incorrectCurrentPassword',
            );

            // Also show toast for visibility
            this.toastService.showToast(
              this.translate.instant('alerts.incorrectCurrentPassword'),
              'error',
            );
          } else if (
            errorTitle === 'PasswordUsedBefore' ||
            errorTitle === 'PasswordUserBefore'
          ) {
            this.toastService.showToast(
              this.translate.instant('alerts.passwordUsedBefore'),
              'error',
            );
          } else {
            this.toastService.showToast(
              this.translate.instant('alerts.passwordUpdateFailed'),
              'error',
            );
          }
        } else if (err.status === 401) {
          this.toastService.showToast(
            this.translate.instant('error.unauthorized'),
            'error',
          );
          this.router.navigate(['/auth/login']);
        } else {
          this.toastService.showToast(
            this.translate.instant('alerts.passwordUpdateFailed'),
            'error',
          );
        }
      },
    });
  }

  onCurrentPasswordChange(): void {
    this.currentPasswordError = null;
  }

  signOut() {
    this.authService.logout().subscribe(() => {
      this.cookieService.deleteCookie('applications');
    });
  }
}
